import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import i18nConfig from '../../i18nConfig';

const { locales: supportedLocales, defaultLocale } = i18nConfig;

export const useLocaleRouter = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { i18n } = useTranslation();

  const segments = pathname?.split('/').filter(Boolean) ?? [];
  const potentialLocale = segments[0];
  const hasLocaleInPath =
    typeof potentialLocale === 'string' && supportedLocales.includes(potentialLocale);

  const fallbackLocale =
    typeof i18n.language === 'string' && supportedLocales.includes(i18n.language)
      ? i18n.language
      : defaultLocale;

  const routeSegments = hasLocaleInPath ? segments.slice(1) : segments;
  const currentLocale = hasLocaleInPath ? potentialLocale : fallbackLocale;

  const createLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    if (!cleanPath) {
      return `/${currentLocale}`;
    }

    return `/${currentLocale}/${cleanPath}`;
  };

  const push = (path: string) => {
    router.push(createLocalizedPath(path));
  };

  const replace = (path: string) => {
    router.replace(createLocalizedPath(path));
  };

  const changeLocale = (newLocale: string) => {
    if (!supportedLocales.includes(newLocale)) {
      return;
    }

    const newPathSegments = [newLocale, ...routeSegments];
    const newPath = `/${newPathSegments.join('/')}`;

    i18n.changeLanguage(newLocale);
    localStorage.setItem('lang', newLocale);

    router.push(newPath);
  };

  return {
    currentLocale,
    createLocalizedPath,
    push,
    replace,
    changeLocale,
  };
};
